import { policyType as PolicyType } from '@ourbranch/lookups';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';

// Data + classification constants for the Validation Errors modal, kept separate so the component
// file stays focused on rendering. The modal re-exports these for consumers/tests (#30230).

// #32032 / #32082: the farm dwelling sections are labeled "Farm risk …" by default, but the
// "Farm" wording reads wrong on Forreston Home (Z) and Rental (Y) — those products aren't "farm".
// On Z/Y the details section becomes "Dwelling details" (#32032) and the coverages section becomes
// "Risk coverages" (#32082). Full Farm (F), and any non-farm/unknown context, keep the farm labels.
export const FARM_RISK_DETAILS_LABEL = 'Farm risk details';
export const DWELLING_DETAILS_LABEL = 'Dwelling details';
export const FARM_RISK_COVERAGES_LABEL = 'Farm risk coverages';
export const RISK_COVERAGES_LABEL = 'Risk coverages';
// #32212: the liability, location, and policy-wide-coverages sections likewise drop the "Farm"
// prefix on Forreston Home (Z) and Rental (Y) — the real form headings never say "Farm" for those
// sections, so the modal card labels shouldn't either.
export const FARM_LIABILITY_LABEL = 'Farm liability';
export const LIABILITY_LABEL = 'Liability';
export const FARM_LOCATION_LABEL = 'Farm location';
export const LOCATIONS_LABEL = 'Locations';
export const FARM_POLICY_WIDE_COVERAGES_LABEL = 'Farm policy-wide coverages';
export const POLICY_WIDE_ENDORSEMENTS_LABEL = 'Policy-wide endorsements';
// #32229: the people on ANY farm-based product (F/Z/Y) are insureds, not drivers — the age
// validation card must read "Insured(s)" rather than "Drivers".
export const DRIVERS_LABEL = 'Drivers';
export const INSUREDS_LABEL = 'Insured(s)';

// Labels that drop their "Farm" wording on Forreston Home (Z) / Rental (Y) only. Full Farm (F) keeps
// them. Keyed by the default farm label → its non-farm replacement. (#32032, #32082, #32212)
const NON_FARM_LABEL_REMAP = {
  [FARM_RISK_DETAILS_LABEL]: DWELLING_DETAILS_LABEL,
  [FARM_RISK_COVERAGES_LABEL]: RISK_COVERAGES_LABEL,
  [FARM_LIABILITY_LABEL]: LIABILITY_LABEL,
  [FARM_LOCATION_LABEL]: LOCATIONS_LABEL,
  [FARM_POLICY_WIDE_COVERAGES_LABEL]: POLICY_WIDE_ENDORSEMENTS_LABEL
};

// Labels remapped for EVERY farm-based product (F/Z/Y), including full Farm — the concept itself is
// wrong on a property/farm policy, not just the "Farm" wording. (#32229)
const FARM_BASED_LABEL_REMAP = {
  [DRIVERS_LABEL]: INSUREDS_LABEL
};

// Resolve a section's display label for the current offer/policy. "Drivers" → "Insured(s)" on any
// farm-based product (#32229); the dwelling/liability sections additionally drop their "Farm"
// wording on Home/Rental only (#32032, #32082, #32212). Other labels pass through unchanged.
export const resolveSectionLabel = (label, offerPolicyType) => {
  if (isFarmBasedPolicyType(offerPolicyType) && label in FARM_BASED_LABEL_REMAP) {
    return FARM_BASED_LABEL_REMAP[label];
  }
  if (
    (offerPolicyType === PolicyType.FarmHome || offerPolicyType === PolicyType.FarmRental) &&
    label in NON_FARM_LABEL_REMAP
  ) {
    return NON_FARM_LABEL_REMAP[label];
  }
  return label;
};

// Synthetic Yup error a validation schema may set when a selected option has a blocking
// rejection that can't be fixed inline (e.g. an offer that fails underwriting). When present
// it renders in the Blocking issues section, never in the Fixable issues cards. This modal is
// a core component shared by the offer and policy flows, so the field is keyed by name rather
// than tied to any one schema.
export const REJECTIONS_ERROR_FIELD = 'oneOrMorePoliciesOnThisOfferCannotBePurchased';

// Title of the single Blocking-issues card and the key its go-to target is looked up under in
// SECTION_NAV_TARGETS. Kept as a constant so the rendered title and the nav lookup can't drift.
export const REJECTIONS_SECTION_LABEL = 'Offer rejections';

// DOM id of the always-mounted offer-rejections block in the offer sidebar
// (offer/.../sidebar/.../policy-errors). Used both as the Blocking card's scroll anchor and as the
// fallback target when a fixable card's tab is unreachable on a rejected offer (see offer.jsx B9).
export const REJECTIONS_NAV_ANCHOR = 'offer_rejections';

// Issue #34087: a farm offer that insures nothing (no risks on any location AND farm liability off)
// is a blocking issue too, and the farm schema raises it on this dedicated field. It deliberately
// does NOT reuse REJECTIONS_ERROR_FIELD: rejectionsSchema writes that same path from a shape-field
// test, which Yup runs before whole-object `.test()`s, and Formik keeps only the first error per
// path — so an active rejection would silently swallow this warning at the very UW-approval step it
// targets. Its own field lets both blocking issues surface. The field name is kept in sync with the
// farm schema (common/validation-schemas/farm-shared.validation-schema.js).
export const FARM_INSURES_NOTHING_FIELD = 'farmInsuresNothing';

// Title of the insures-nothing Blocking card, and the key its go-to target is looked up under in
// SECTION_NAV_TARGETS.
export const FARM_INSURES_NOTHING_SECTION_LABEL = 'Coverage required';

// Issue #35914: CFM Auto must accompany a primary homeowner policy with CFM. When "Primary Homeowner
// policy with CFM?" isn't answered Yes, the offer and policy-details auto validation schemas raise a
// blocking error on this dedicated synthetic field so the modal renders it in the red "Blocking
// issues" section (same pattern as the farm insures-nothing field, and a distinct path so neither can
// swallow the other). The field name is kept in sync with the auto validation schemas
// (offer/provider/validation-schemas/auto.validation-schema.js and
// customer/components/policy/details/validations/auto.validation-schema.js).
export const MISSING_PRIMARY_HOMEOWNER_FIELD = 'missingPrimaryHomeownerPolicy';

// Title of the missing-primary-homeowner Blocking card. The schema's error message ("Must have Primary
// Homeowner Policy with CFM") renders below it as the card's detail.
export const MISSING_PRIMARY_HOMEOWNER_SECTION_LABEL = 'Primary homeowner policy required';

// #36353: the offer form couldn't run the Staff Quote Revisit validation rules (a code chunk failed
// to fetch after a deploy, or a lane rule threw), so it can't confirm the quote is valid. Raised as a
// blocking error so Update quote and Continue to checkout stop instead of going through unchecked.
// Set by the offer provider from quote-revisit/validation's QuoteRevisitValidationError.
export const QUOTE_VALIDATION_UNAVAILABLE_FIELD = 'quoteValidationUnavailable';

export const QUOTE_VALIDATION_UNAVAILABLE_SECTION_LABEL = "Quote check didn't run";

// Fixable-issue cards per the design's error classification matrix (#30230 Figma board,
// node 60239-44532). Order here = card display order in the modal. An error path is
// classified by its most specific matching prefix; '*' matches any single path segment
// (e.g. an array index). Paths with no match land in the 'Other issues' card, always last.
// Prefixes cover both the offer schemas and the customer-policy schemas, since this modal is
// shared by both flows (some concepts use different top-level keys between them).
export const SECTION_RULES = [
  { label: DRIVERS_LABEL, prefixes: ['drivers'] },
  { label: 'Household members', prefixes: ['auto.pipResidentsConfirmation'] },
  { label: 'Removed drivers', prefixes: ['removedDrivers'] },
  { label: 'PIP coverage', prefixes: ['auto.pipEligibilityConfirmation'] },
  { label: 'Home details', prefixes: ['home'] },
  { label: 'Condo details', prefixes: ['condo'] },
  { label: 'Home policy coverage', prefixes: ['homeCoverage'] },
  { label: 'Condo policy coverage', prefixes: ['condoCoverage'] },
  { label: 'Auto policy coverages', prefixes: ['autoCoverage'] },
  { label: 'Connected home', prefixes: ['connectedHome'] },
  { label: 'People on policy', prefixes: ['people'] },
  { label: 'Vehicles details', prefixes: ['cars'] },
  { label: 'Trailers details', prefixes: ['trailers'] },
  // Mortgage lender errors live in two places: the top-level `primaryMortgageDetail` (escrow
  // validation on the offer) and the inline mortgage FormList on the Home/Condo tabs
  // (`home.mortgageDetails[i]` / `condo.mortgageDetails[i]`). The inline paths start with `home`/
  // `condo`, so without these more-specific prefixes they'd fall into the "Home details"/"Condo
  // details" card and (per the nav fix below) scroll to the wrong place. Most-specific prefix wins,
  // so these take precedence over the bare `home`/`condo` rules above.
  {
    label: 'Mortgage details',
    prefixes: ['primaryMortgageDetail', 'home.mortgageDetails', 'condo.mortgageDetails']
  },
  { label: 'Policy settings', prefixes: ['global', 'directBillPreferredPaymentMethod', 'farmInterestedParties'] },
  { label: 'Interested parties', prefixes: ['interestedParties'] },
  { label: 'Lienholders', prefixes: ['lienHolders'] },
  { label: 'Renters coverage', prefixes: ['rentersCoverage'] },
  { label: 'Umbrella', prefixes: ['includeUmbrella', 'umbrellaCoverage'] },
  { label: 'Scheduled personal property', prefixes: ['scheduledPersonalProperty'] },
  { label: FARM_LOCATION_LABEL, prefixes: ['farmLocations'] },
  { label: FARM_RISK_DETAILS_LABEL, prefixes: ['farmLocations.*.risks'] },
  { label: FARM_RISK_COVERAGES_LABEL, prefixes: ['farmLocations.*.risks.*.coverages'] },
  { label: FARM_LIABILITY_LABEL, prefixes: ['farmLiabilityCoverage'] },
  { label: FARM_POLICY_WIDE_COVERAGES_LABEL, prefixes: ['farmCoverage'] },
  { label: 'Entity details', prefixes: ['entity'] }
];

export const OTHER_ISSUES_LABEL = 'Other issues';

// Mortgage-lender merge (designer PR feedback, Jun 16). When the home/condo/farm is escrowed, the
// schema validates each lender field separately (`primaryMortgageDetail.mortgageHolderName`,
// `...mortgageHolderAddress` and its sub-fields), so a blank lender produced up to five rows
// ("Mortgage lender name is required", "...address is required", "City is required", …). The
// designer wants those collapsed into ONE row in the modal. Loan number is deliberately NOT merged
// — it's the one lender field a user can legitimately leave empty, so it keeps its own row.
//
// This is done at the modal-display layer (groupValidationErrors), NOT in the schema: the same
// `primaryMortgageDetail.*` field errors are bound to the Settings-tab mortgage inputs for inline
// highlighting, so collapsing them in Yup would strip those per-field errors off the form.
export const MERGED_MORTGAGE_FIELD = 'primaryMortgageDetail';
export const MERGED_MORTGAGE_LOAN_NUMBER_FIELD = 'primaryMortgageDetail.loanNumber';
export const MERGED_MORTGAGE_MESSAGE = 'Mortgage lender information is required because the payment method is escrow.';

// Where the "go to section" arrow scrolls for each card, per flow. The modal groups errors by
// human label, but the form's scroll anchors are the DOM ids that core `Section`/`FormList`
// render (`<div id=...>`). Those ids are NOT the labels, and a few differ between the offer and
// policy-details flows, so this map is the single source of truth for that translation.
//
//   offer:  { anchor, tab } — offer is tabbed; tab must be switched before the anchor is mounted.
//           `tab` is an OfferPageTabs value (see core/store/offer-store). Switch via store.setCurrentTab.
//   policy: { anchor }      — policy-details is a single page (no tabs), so no `tab`.
//
// A label with no entry for a flow (or a null anchor) has NO reliable scroll target in that flow,
// so its card renders without a go-to arrow. Connected home is omitted on purpose: on offer it's a
// sidebar "add discount" modal (no inline section to scroll to) and on policy it renders inside the
// coverage area with no standalone anchor. The coverage Sections (auto/home/condo) and farm risk
// sub-sections likewise have no own id yet and are omitted until they get one.
//
// v1 scope: offer + policy-details only. Farm sub-risk dialogs and the offer discount modals are
// out — their cards get no arrow.
export const SECTION_NAV_TARGETS = {
  // The single Blocking-issues card. It scrolls to the sidebar's offer-rejections block
  // (`id="offer_rejections"` in offer/.../sidebar/.../policy-errors), which is always mounted and
  // not behind a tab — so no `tab` is needed. Offer-only: the policy-details route has no rejections
  // sidebar, so the policy card stays arrow-less. (designer PR feedback, Jun 16)
  [REJECTIONS_SECTION_LABEL]: { offer: { anchor: REJECTIONS_NAV_ANCHOR } },
  [DRIVERS_LABEL]: { offer: { anchor: 'drivers', tab: 'PEOPLE' }, policy: { anchor: 'drivers' } },
  // #32229: on farm-based products the Drivers card renders as "Insured(s)"; the nav lookup runs on
  // that relabeled name, so it points at the same drivers anchor to keep the go-to arrow.
  [INSUREDS_LABEL]: { offer: { anchor: 'drivers', tab: 'PEOPLE' }, policy: { anchor: 'drivers' } },
  'Household members': { offer: { anchor: 'nonDrivers', tab: 'PIP' }, policy: { anchor: 'nonDrivers' } },
  'PIP coverage': {
    offer: { anchor: 'personalInjuryProtectionSection', tab: 'PIP' },
    policy: { anchor: 'personalInjuryProtectionSection' }
  },
  // Home/Condo details scroll to the TOP of the home/condo section (the `id="home"`/`id="condo"`
  // anchor on the details Section), NOT to the mortgage subsection. Previously both of these and
  // "Mortgage details" all pointed at `*.mortgageDetails`, so a home-detail error (e.g. roof year)
  // would scroll past the home form down to the mortgage lender block (designer PR feedback, Jun 16).
  'Home details': {
    offer: { anchor: 'home', tab: 'HOME' },
    policy: { anchor: 'home' }
  },
  'Condo details': {
    offer: { anchor: 'condo', tab: 'CONDO' },
    policy: { anchor: 'condo' }
  },
  'Mortgage details': {
    offer: { anchor: 'home.mortgageDetails', tab: 'HOME' },
    policy: { anchor: 'home.mortgageDetails' }
  },
  'Auto policy coverages': { offer: { anchor: 'autoCoverage', tab: 'AUTO' }, policy: { anchor: 'autoCoverage' } },
  'People on policy': { policy: { anchor: 'people' } },
  'Vehicles details': { offer: { anchor: 'cars', tab: 'AUTO' }, policy: { anchor: 'cars' } },
  'Trailers details': { offer: { anchor: 'trailers', tab: 'AUTO' }, policy: { anchor: 'trailers' } },
  // Interested parties and lien holders are only editable on the OFFER (home/condo/auto-offer
  // components). The policy-details route has no such section, so these are offer-only — their
  // policy card shows no arrow.
  'Interested parties': { offer: { anchor: 'interestedParties', tab: 'HOME' } },
  Lienholders: { offer: { anchor: 'lienHolders', tab: 'AUTO' } },
  // Renters endorsement on an auto offer/policy — the shared auto/renters component carries the
  // `rentersCoverage` Section id. (A standalone renters policy is a different, non-tabbed surface.)
  'Renters coverage': { offer: { anchor: 'rentersCoverage', tab: 'AUTO' }, policy: { anchor: 'rentersCoverage' } },
  Umbrella: { offer: { anchor: 'includeUmbrella', tab: 'HOME' }, policy: { anchor: 'includeUmbrella' } },
  'Scheduled personal property': { offer: { anchor: 'sppContainer', tab: 'HOME' }, policy: { anchor: 'sppContainer' } },
  // Entity details is wrapped in <div id="entity"> only at the offer people-details render site;
  // the policy farm page renders EntityDetailsFields bare with no anchor, so offer-only for now.
  'Entity details': { offer: { anchor: 'entity', tab: 'PEOPLE' } },
  // One card holds every `global.*` error across products, so it targets the Billing tab wrapper
  // (<div id="policySettings"> in offer Settings), not a single product's settings card.
  'Policy settings': { offer: { anchor: 'policySettings', tab: 'SETTINGS' } },
  // #32212: Rental (Y) / Home (Z) display "Locations", "Liability", and "Policy-wide endorsements"
  // instead of the "Farm …" labels. Both keys share the same target object so anchors can't drift.
  [FARM_LOCATION_LABEL]: { offer: { anchor: 'farmLocations', tab: 'FARM' }, policy: { anchor: 'farmLocations' } },
  [LOCATIONS_LABEL]: { offer: { anchor: 'farmLocations', tab: 'FARM' }, policy: { anchor: 'farmLocations' } },
  [FARM_LIABILITY_LABEL]: { offer: { anchor: 'farmLiability', tab: 'FARM' }, policy: { anchor: 'farmLiability' } },
  [LIABILITY_LABEL]: { offer: { anchor: 'farmLiability', tab: 'FARM' }, policy: { anchor: 'farmLiability' } },
  [FARM_POLICY_WIDE_COVERAGES_LABEL]: {
    offer: { anchor: 'farmPolicyWideEndorsements', tab: 'FARM' },
    policy: { anchor: 'farmPolicyWideEndorsements' }
  },
  [POLICY_WIDE_ENDORSEMENTS_LABEL]: {
    offer: { anchor: 'farmPolicyWideEndorsements', tab: 'FARM' },
    policy: { anchor: 'farmPolicyWideEndorsements' }
  },
  // #34087: the insures-nothing blocking card sends the user to the farm locations section, where
  // they add a risk or toggle liability to fix it.
  [FARM_INSURES_NOTHING_SECTION_LABEL]: {
    offer: { anchor: 'farmLocations', tab: 'FARM' },
    policy: { anchor: 'farmLocations' }
  }
};

// Resolve a card's go-to target for the given flow ('offer' | 'policy').
// Returns { anchor, tab? } or null when the section has no scroll anchor in that flow
// (caller should then hide the go-to arrow for that card).
export const getSectionNavTarget = (label, flow) => SECTION_NAV_TARGETS[label]?.[flow] ?? null;
